import classNames from "classnames";
import DOMPurify from "dompurify";
import React, { useState } from "react";
import { GoTrash } from "react-icons/go";
import { MdBolt } from "react-icons/md";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { AvatarPlaceholder } from "../../../../../components/avatar/AvatarPlaceholder";
import { Dropdown } from "../../../../../components/dropdown/new-dropdown/Dropdown";
import { SettingsDropdown } from "../../../../../components/dropdown/SettingsDropdown";
import { Avatar } from "../../../../../components/newUI/avatar";
import { DotsSvg } from "../../../../../components/svg/DotsSvg";
import { DraggableSvg } from "../../../../../components/svg/DraggableSvg";
import { EditPencilSvg } from "../../../../../components/svg/EditPencilSvg";
import { getInitials } from "../../../../../constants/constants";
import { WorkTemplateTaskModel } from "../../../../../factories/templates/models/template.model";
import { getLinkedFirm } from "../../../../../sessionStorage/sessionStorage";
import { useGetUsersQuery } from "../../../../../slices/UserSlice";
import { IUser } from "../../../../../types/types";
import {
  DUE_DAYS_ERROR,
  DUE_DAYS_MAX,
  DUE_DAYS_MIN,
  parseDueDays,
} from "../../../../../utils/dueDays";
import { SubtaskForm } from "./SubtaskForm";

type DraftSubtask = {
  id: string;
  name: string;
  description: string;
  budgetedTime: string;
  dueDays: string;
  dueAfterStart: boolean;
  assignedUserId?: string;
  assignedUser?: WorkTemplateTaskModel["assignedUser"];
  isNew?: boolean;
};

type Props = {
  subtask: WorkTemplateTaskModel | DraftSubtask;
  order: number;
  getTemplate?: () => Promise<void>;
  isDraft?: boolean;
  onEditDraft?: (subtask: DraftSubtask) => void;
  onDeleteDraft?: (subtaskId: string) => void;
  budgetedTime?: string;
  automationTooltip?: string;
  isAutomationDeleting?: boolean;
  onBudgetChange?: (subtaskId: string, budgetedTime: string) => void;
  /** "Due: N days after start / before due" (Roadmap 1849); "" = the work's due date. */
  dueDays?: string;
  dueAfterStart?: boolean;
  onDueChange?: (
    subtaskId: string,
    dueDays: string,
    dueAfterStart: boolean,
  ) => void;
  onEditSubtask?: (subtask: DraftSubtask) => void;
  onAssignUser?: (subtaskId: string, user: IUser) => void;
  onAddAutomation?: (subtask: WorkTemplateTaskModel) => void;
  onDeleteAutomation?: (subtask: WorkTemplateTaskModel) => void;
};

export const Subtask: React.FC<Props> = ({
  subtask,
  order,
  isDraft = false,
  onEditDraft,
  onDeleteDraft,
  budgetedTime = "0",
  automationTooltip,
  isAutomationDeleting,
  onBudgetChange,
  dueDays = "",
  dueAfterStart = true,
  onDueChange,
  onEditSubtask,
  onAssignUser,
  onAddAutomation,
  onDeleteAutomation,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const automationTooltipId = `template-task-automation-${subtask.id}`;

  const { data: users } = useGetUsersQuery(getLinkedFirm()?.orgId);

  // Unsaved subtasks can be assigned too; it's saved with the template.
  const handleAssignUser = (user: IUser) => {
    onAssignUser?.(subtask.id, user);
  };

  const handleDelete = () => {
    onDeleteDraft?.(subtask.id);
  };

  const handleEdit = () => {
    if (isDraft) {
      onEditDraft?.(subtask as DraftSubtask);
      return;
    }

    setIsEdit(true);
  };

  const hasAutomation = Boolean(automationTooltip);
  const dueIsValid = parseDueDays(dueDays).ok;
  const descriptionText = subtask.description
    ? subtask.description.replace(/<[^>]+>/g, "").trim()
    : "";

  if (isEdit && !isDraft) {
    return (
      <SubtaskForm
        order={order}
        defaultValue={subtask as WorkTemplateTaskModel}
        onClose={() => setIsEdit(false)}
        isLocalMode={Boolean(onEditSubtask)}
        onSubmitLocal={(data) => {
          onEditSubtask?.({
            ...(subtask as DraftSubtask),
            name: data.name,
            description: data.description,
          });
          setIsEdit(false);
        }}
      />
    );
  }

  return (
    <div
      className={
        "transitions-all group mb-2 grid w-full grid-cols-[minmax(0,1fr)_128px_208px_114px_32px] items-center gap-4 rounded-xl bg-[#FAFAFC] p-3 duration-300 last:mb-0 hover:bg-[#4C637B14]"
      }>
      <div className={"flex min-w-0 items-center gap-2"}>
        <div className={" text-[#253747]"}>
          <DraggableSvg />
        </div>

        <div
          className={
            "flex h-6 w-fit min-w-[24px] items-center justify-center rounded-full bg-[#D9D6FE] px-2 text-sm text-[#5925DC]"
          }>
          {order}
        </div>

        <div className={"min-w-0 flex-1 overflow-hidden"}>
          <div
            className={"w-full truncate text-base font-semibold text-[#1D2C3B]"}
            title={subtask.name}>
            {subtask.name}
          </div>

          <div
            className={
              "rich-text-content w-full overflow-hidden break-words text-base text-[#3F5266] [-webkit-box-orient:vertical] [-webkit-line-clamp:3] [display:-webkit-box]"
            }
            title={descriptionText}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(subtask.description || ""),
            }}
          />
        </div>
      </div>

      <div className={"grid grid-cols-[20px_72px_20px] items-center gap-2"}>
        <div className={"flex h-5 w-5 items-center justify-center"}>
          {automationTooltip && (
            <MdBolt
              className={"h-5 w-5 shrink-0 text-yellow-300"}
              data-tooltip-id={automationTooltipId}
              data-tooltip-content={automationTooltip}
            />
          )}
          <ReactTooltip
            id={automationTooltipId}
            place={"top"}
            className={"!max-w-[260px] !text-xs !leading-4"}
          />
        </div>
        <input
          type={"number"}
          min={0}
          step={1}
          inputMode={"numeric"}
          value={budgetedTime}
          onChange={(event) =>
            onBudgetChange?.(subtask.id, event.target.value || "0")
          }
          className={
            "h-8 w-[72px] rounded-lg border border-[#D0D5DD] bg-white px-2 text-right text-sm font-medium text-[#1D2C3B] focus:border-[#6938EF] focus:outline-none"
          }
        />
        <span className={"text-sm font-medium text-[#3F5266]"}>hrs</span>
      </div>

      <div
        className={"flex items-center gap-1.5"}
        title={
          dueIsValid
            ? "Due date of this subtask when the template is added to a work item. Empty = the work's due date."
            : DUE_DAYS_ERROR
        }>
        <input
          type={"number"}
          min={DUE_DAYS_MIN}
          max={DUE_DAYS_MAX}
          step={1}
          inputMode={"numeric"}
          placeholder={"–"}
          aria-label={"Due: days"}
          value={dueDays}
          onChange={(event) =>
            onDueChange?.(subtask.id, event.target.value, dueAfterStart)
          }
          className={classNames(
            "h-8 w-[56px] rounded-lg border bg-white px-2 text-right text-sm font-medium text-[#1D2C3B] focus:outline-none",
            dueIsValid
              ? "border-[#D0D5DD] focus:border-[#6938EF]"
              : "border-[#DC2626]",
          )}
        />
        <select
          aria-label={"Due: after start or before due"}
          value={dueAfterStart ? "after" : "before"}
          onChange={(event) =>
            onDueChange?.(subtask.id, dueDays, event.target.value === "after")
          }
          className={
            "h-8 min-w-0 flex-1 rounded-lg border border-[#D0D5DD] bg-white px-1.5 text-sm text-[#3F5266] focus:border-[#6938EF] focus:outline-none"
          }>
          <option value={"after"}>days after start</option>
          <option value={"before"}>days before due</option>
        </select>
      </div>

      <div className={"flex justify-end"}>
        {!onAssignUser ? (
          <div
            className={
              "min-w-0 flex-1 truncate text-right text-sm font-semibold text-[#98A2B3]"
            }
            title={"Assign after save"}>
            Assign after save
          </div>
        ) : (
          <div className={"flex items-center"}>
            <Dropdown
              isCloseOnClick
              triggerElement={
                !(subtask as WorkTemplateTaskModel).assignedUserId ? (
                  <div className={"min-w-[65px] text-sm font-semibold"}>
                    Assign to
                  </div>
                ) : (
                  <Avatar
                    label={getInitials(
                      (subtask as WorkTemplateTaskModel).assignedUser
                        ?.userProfile?.firstName || "",
                      (subtask as WorkTemplateTaskModel).assignedUser
                        ?.userProfile?.lastName || "",
                    )}
                    id={subtask.assignedUser?.id || subtask.id}
                    color={
                      (subtask as WorkTemplateTaskModel).assignedUser
                        ?.userProfile?.color
                    }
                    size={"md"}
                    fullName={`${
                      (subtask as WorkTemplateTaskModel).assignedUser
                        ?.userProfile?.firstName || ""
                    } ${
                      (subtask as WorkTemplateTaskModel).assignedUser
                        ?.userProfile?.lastName || ""
                    }`}
                  />
                )
              }>
              <div
                className={
                  "rounded-[5px] border border-gray-300 bg-white text-sm shadow-box"
                }>
                <div
                  className={
                    "link-primary border-b-[1px] border-b-primary px-4 py-2 text-sm"
                  }>
                  Reassign subtask to:
                </div>

                <div className={"max-h-[200px] overflow-y-auto"}>
                  {users?.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleAssignUser(item)}
                      className={
                        "flex cursor-pointer items-center gap-1 px-4 py-2 hover:bg-blue-400 hover:text-white"
                      }>
                      <AvatarPlaceholder
                        size={"super-small"}
                        type={"custom"}
                        avatarCustomColor={item.userProfile?.color}
                        label={getInitials(
                          item.userProfile?.firstName || "",
                          item.userProfile?.lastName || "",
                        )}
                      />
                      <div className={"whitespace-pre-wrap"}>
                        {`${item.userProfile?.firstName || ""} ${
                          item.userProfile?.lastName || ""
                        }`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Dropdown>
          </div>
        )}
      </div>
      <SettingsDropdown
        actionsList={[
          {
            name: "Edit",
            action: handleEdit,
            icon: <EditPencilSvg />,
          },
          ...(onAddAutomation
            ? [
                hasAutomation
                  ? {
                      name: "Delete Automation",
                      action: () =>
                        onDeleteAutomation?.(subtask as WorkTemplateTaskModel),
                      icon: <MdBolt size={18} />,
                      disabled: isAutomationDeleting,
                    }
                  : {
                      name: "Add Automation",
                      action: () =>
                        onAddAutomation?.(subtask as WorkTemplateTaskModel),
                      icon: <MdBolt size={18} />,
                    },
              ]
            : []),
          {
            name: "Delete",
            action: handleDelete,
            icon: <GoTrash size={"18px"} />,
            extraClasses: "text-[#DC2626]",
            iconColor: "text-[#DC2626]",
          },
        ]}>
        <button
          type={"button"}
          className={
            "flex h-8 w-8 items-center justify-center rounded-lg text-[#A1AFC0] transition hover:bg-[#4C637B0D] hover:text-[#253747]"
          }>
          <DotsSvg />
        </button>
      </SettingsDropdown>
    </div>
  );
};
